import React, { useState } from "react";
import { getInitials } from "../../../common/common";

const formatPlacementDate = (dateOfPlacement) => {
    if (!dateOfPlacement) return "-";

    const date = new Date(dateOfPlacement);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const PlacedCandidateItem = ({
    candidate,
    selectedIds,
    onSelectionChange,
    showCheckbox
}) => {
    const [isHovering, setIsHovering] = useState(false);

    const isSelected = selectedIds.some(item => item.id === candidate.id);

    return (
        <div
            className="relative bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-all cursor-pointer flex gap-2 sm:gap-4 items-start min-w-0" // Added responsive padding/gap, min-w-0
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={() =>
                onSelectionChange(candidate.id, candidate.email, !isSelected)
            }
        >
            {/* LEFT SIDE CHECKBOX */}
            {(showCheckbox || isHovering || isSelected) && (
                <button
                    type="button"
                    disabled={candidate.feedback_mail}
                    onClick={(e) => {
                        if (candidate.feedback_mail) return; 
                        e.stopPropagation();
                        onSelectionChange(candidate.id, candidate.email, !isSelected);
                    }}
                    className={`flex-shrink-0 mt-1 w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center border transition-all
                ${candidate.feedback_mail
                            ? "bg-gray-200 border-gray-300 cursor-not-allowed opacity-50"
                            : isSelected
                                ? "bg-emerald-600 border-emerald-600"
                                : "bg-white border-gray-300"
                        }
            `}
                >
                    {isSelected && !candidate.feedback_mail && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="sm:w-3.5 sm:h-3.5">
                            <path
                                d="M20 6L9 17l-5-5"
                                stroke="#fff"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                </button>
            )}
            <div className="w-full min-w-0 flex flex-col gap-2 sm:gap-4"> {/* Added min-w-0 and flex structure */}
                {/* TOP ROW: Avatar + Name/Status */}
                <div className="flex items-start items-center gap-2 sm:gap-4 min-w-0"> {/* flex with min-w-0 */}
                    <div className="flex-shrink-0">
                        {/* Avatar Circle - responsive size */}
                        <div
                            className="rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                            style={{
                                width: "40px",
                                height: "40px",
                                background: "#F3F4F6"
                                }}
                        >
                            {candidate.avatar ? (
                                <img
                                    src={candidate.avatar}
                                    alt={candidate?.full_name ??  `${candidate.first_name || ""} ${candidate.last_name || ""}`}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span
                                    style={{
                                        fontFamily: "Raleway, sans-serif",
                                        fontWeight: 800,
                                        fontSize: "16px", // Reduced from 20px
                                        color: "#246B6A",
                                    }}
                                >
                                    {getInitials(candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Name and Feedback Status */}
                    <div className="flex flex-col flex-1 min-w-0"> {/* flex-1 min-w-0 for truncation */}
                        <div className="flex items-center gap-1 sm:gap-3 min-w-0">
                            <span title={   candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`}  className="text-sm sm:text-base font-semibold text-gray-900 truncate min-w-0 flex-1">
                                {candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`}
                            </span>
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0
                                ${candidate.feedback_mail 
                                    ? "text-emerald-700 bg-emerald-50" 
                                    : "text-gray-700 bg-gray-100"
                                }`}>
                                {candidate.feedback_mail ? "Feedback Sent" : "Feedback Pending"}
                            </span>
                        </div>
                    </div>
                </div>

                {/* BOTTOM ROW: Email + Phone + Placement Date - responsive flex */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 min-w-0">
                    {/* Email */}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-999 mb-1">Email</p>
                        <div className="text-sm text-gray-333 truncate" title={candidate.email}>
                            {candidate.email}
                        </div>
                    </div>
                    
                    {/* Phone */}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-999 mb-1">Contact number</p>
                        <p
                            className="font-semibold text-gray-333 text-sm truncate"
                            title={candidate.phone || "-"}
                        >
                            {candidate.phone || "-"}
                        </p>
                    </div>

                    {/* Date of placement */}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-999 mb-1">Date of placement</p>
                        <p
                            className="font-semibold text-gray-333 text-sm truncate"
                            title={formatPlacementDate(candidate.date_of_placement)}
                        >
                            {formatPlacementDate(candidate.date_of_placement)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlacedCandidateItem;
